import { Form, Input, Button, Radio, InputNumber } from 'antd';
import 'dayjs/locale/ja';
import {
  CreatePatientDto,
  Patient,
  UpdatePatientDto,
} from '../../../../models';
import { formatYYYYMMDD } from '../../../../utils/date';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { IndependentLifeLevel } from '../../../../constants/independentLifeLevel';
import { PatientCareLevel } from '../../../../constants/careLevel';
import useLoading from '../../../../hooks/useLoading';
import { DentalIntervention } from '../../../../constants/dentalIntervention';
import { useTranslation } from 'react-i18next';

export type Props = {
  onSubmit: (values: CreatePatientDto | UpdatePatientDto) => void;
  initialValue?: Patient;
  onCancel?: () => void;
  failedCallback?: (info: any) => void;
};
export const PatientForm = (props: Props) => {
  const { initialValue, onSubmit, onCancel, failedCallback = () => {} } = props;
  const { t, i18n } = useTranslation();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [form] = Form.useForm();

  const lifeLevelOptions = useMemo(
    () =>
      IndependentLifeLevel.getAll().map((v) => ({
        key: v.key,
        value: v.key,
        label: v.getLabel(i18n.language),
      })),
    [i18n.language],
  );
  const careLevelOptions = useMemo(
    () =>
      PatientCareLevel.getAll().map((v) => ({
        key: v.key,
        value: v.key,
        label: v.getLabel(i18n.language),
      })),
    [i18n.language],
  );
  const dentalInterventionOptions = useMemo(
    () =>
      DentalIntervention.getAll().map((v) => ({
        key: v.key,
        value: v.key,
        label: v.getLabel(i18n.language),
      })),
    [i18n.language],
  );

  useEffect(() => {
    if (initialValue) {
      form.setFieldsValue({
        name: initialValue.name,
        age: initialValue.age,
        birthDate: dayjs(initialValue.birthDate),
        gender: initialValue.gender,
        careLevel: initialValue.careLevel,
        independentLifeLevel: initialValue.independentLifeLevel,
        dentalIntervention: initialValue.dentalIntervention,
        memo: initialValue.memo,
      });
    }
  }, [form, initialValue]);

  const onFinish = async (values: CreatePatientDto) => {
    values.birthDate = formatYYYYMMDD(values.birthDate);
    startLoading();
    await onSubmit(values);
    stopLoading();
  };

  const onFinishFailed = (errorInfo: any) => {
    failedCallback(errorInfo);
  };

  return (
    <Form
      form={form}
      name="patientForm"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      style={{ width: '100%' }}
      layout="vertical"
    >
      <Form.Item
        label={t('patient.name')}
        name="name"
        rules={[{ required: true, message: t('common.required') }]}
        style={{ marginBottom: 4 }}
      >
        <Input placeholder={t('patient.namePlaceholderForm')} />
      </Form.Item>
      <div style={{ color: '#999', fontSize: '12px', marginBottom: 16 }}>
        {t('patient.nameHint')}
      </div>

      <Form.Item label={t('patient.age')} name="age">
        <InputNumber min={0} max={150} />
      </Form.Item>

      {/* <Form.Item
        label={t('patient.birthDate')}
        name="birthDate"
        initialValue={getDefaultPatientBirthYear()}
        rules={[{ required: true, message: t('common.required') }]}
      >
        <DatePicker locale={locale} />
      </Form.Item> */}

      <Form.Item
        label={t('patient.gender')}
        name="gender"
        initialValue={'male'}
        rules={[{ required: true, message: t('common.required') }]}
      >
        <Radio.Group>
          <Radio.Button value="male">{t('common.male')}</Radio.Button>
          <Radio.Button value="female">{t('common.female')}</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={t('patient.careLevel')}
        name="careLevel"
        initialValue={PatientCareLevel.none.key}
        rules={[{ required: true, message: t('common.required') }]}
      >
        <Radio.Group>
          {careLevelOptions.map((opt) => (
            <Radio key={opt.key} value={opt.key}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={t('patient.lifeLevel')}
        name="independentLifeLevel"
        initialValue={IndependentLifeLevel.none.key}
        rules={[{ required: true, message: t('common.required') }]}
      >
        <Radio.Group>
          {lifeLevelOptions.map((opt) => (
            <Radio key={opt.key} value={opt.key}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>

        {/* <Select
          showSearch
          placeholder={t('patient.lifeLevelPlaceholder')}
          optionFilterProp="children"
        >
          {lifeLevelOptions.map((opt) => (
            <Select.Option key={opt.key} value={opt.key}>
              {opt.label}
            </Select.Option>
          ))}
        </Select> */}
      </Form.Item>

      <Form.Item
        label={t('patient.dentalIntervention')}
        name="dentalIntervention"
        initialValue={DentalIntervention.none.key}
        rules={[{ required: true, message: t('common.required') }]}
      >
        <Radio.Group>
          {dentalInterventionOptions.map((opt) => (
            <Radio key={opt.key} value={opt.key}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={t('patient.memo')}
        name="memo"
        tooltip={t('patient.memoTooltip')}
        rules={[{ max: 2000, message: t('patient.memoMax') }]}
      >
        <Input.TextArea rows={4} placeholder={t('patient.memoPlaceholder')} />
      </Form.Item>

      <Form.Item>
        <Button
          block
          type="primary"
          size="large"
          shape="round"
          htmlType="submit"
          loading={isLoading}
        >
          {t('common.submit')}
        </Button>
        {onCancel && (
          <Button
            block
            style={{ marginTop: 12 }}
            size="large"
            shape="round"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};
